import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Sparkles, BookOpen } from "lucide-react";
import { createDiagnosis } from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";
import KrishnaLoop from "../components/KrishnaLoop";

const QUESTIONS = [
  {
    q: "How would you describe your online presence today?",
    key: "presence",
    options: [
      { label: "Barely there, hard to find us", score: 2 },
      { label: "We exist, but it's inconsistent", score: 6 },
      { label: "Decent, but not driving business", score: 10 },
      { label: "Strong and working for us", score: 16 },
    ],
  },
  {
    q: "When a new enquiry comes in, how fast do you respond?",
    key: "response",
    options: [
      { label: "Honestly, some slip through", score: 2 },
      { label: "Within a day or two", score: 7 },
      { label: "Same day, most times", score: 12 },
      { label: "Instantly, it's automated", score: 16 },
    ],
  },
  {
    q: "How much of your week goes to admin instead of growth?",
    key: "time",
    options: [
      { label: "Most of it, I'm buried", score: 2 },
      { label: "More than half", score: 6 },
      { label: "A manageable chunk", score: 11 },
      { label: "Very little, it's handled", score: 16 },
    ],
  },
  {
    q: "How well do you know where your leads actually come from?",
    key: "tracking",
    options: [
      { label: "No idea, really", score: 2 },
      { label: "A rough guess", score: 6 },
      { label: "We track the big ones", score: 11 },
      { label: "Clearly measured", score: 16 },
    ],
  },
  {
    q: "How confident are you converting leads into paying customers?",
    key: "conversion",
    options: [
      { label: "We lose a lot along the way", score: 2 },
      { label: "Hit and miss", score: 7 },
      { label: "Fairly steady", score: 12 },
      { label: "Very confident", score: 16 },
    ],
  },
  {
    q: "If you stepped away for two weeks, would growth continue?",
    key: "systems",
    options: [
      { label: "Everything would stall", score: 2 },
      { label: "It would slow a lot", score: 6 },
      { label: "Mostly fine", score: 11 },
      { label: "Runs without me", score: 16 },
    ],
  },
];

const tierFor = (score) => {
  if (score >= 78) return { tier: "Growing calmly", note: "You're already ahead. A few refinements could unlock the last leg of growth." };
  if (score >= 55) return { tier: "Solid, with leaks", note: "The foundation is there, but revenue and hours are slipping away in places." };
  if (score >= 32) return { tier: "Growth is stuck", note: "You're working hard, but the system is fighting you. This is exactly what we fix." };
  return { tier: "Urgent attention", note: "There's significant revenue leaking right now, and a lot of upside once it's sealed." };
};

const REGIONS = ["UK", "US", "UAE", "Qatar", "Saudi", "Australia", "New Zealand"];

function Intro({ onBegin }) {
  return (
    <main className="min-h-screen pt-28 pb-20 relative overflow-hidden flex items-center" data-testid="diagnosis-intro" style={{ background: "#000000" }}>
      {/* Champagne radial atmospherics — mirrors homepage hero */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-16 h-[620px] w-[620px] rounded-full opacity-[0.14] blur-[120px]"
        style={{ background: "radial-gradient(circle, rgba(201,169,97,0.9) 0%, rgba(201,169,97,0) 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 bottom-0 h-[520px] w-[520px] rounded-full opacity-[0.10] blur-[110px]"
        style={{ background: "radial-gradient(circle, rgba(245,220,160,0.75) 0%, rgba(245,220,160,0) 70%)" }}
      />
      {/* Ambient technical grid */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(201,169,97,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.35) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="font-sans text-[11px] uppercase tracking-[0.5em] text-brass mb-8 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-brass/60" />
          A 60-second business diagnosis. Free and direct.
        </motion.p>
        <h1
          className="max-w-4xl"
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: "clamp(1.9rem, 6vw, 5.2rem)",
            lineHeight: 1.08,
            letterSpacing: "-0.02em",
            fontWeight: 500,
            color: "#F5F1E8",
          }}
        >
          <DiagWave text="Where is your growth" />
          <br />
          <span style={{ fontStyle: "italic" }}>
            <DiagWave text="leaking?" delay={0.35} goldWords={["leaking"]} />
          </span>
        </h1>
        <p
          className="mt-8 max-w-2xl"
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: "clamp(1.1rem, 2.4vw, 1.7rem)",
            lineHeight: 1.4,
            color: "rgba(245,241,232,0.75)",
          }}
        >
          <DiagWave
            text="Answer six honest questions. I will show you your growth health, then record a free diagnosis of your business. No auto reports. No strings attached."
            delay={0.9}
          />
        </p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.6 }} className="mt-12 flex flex-wrap items-center gap-6">
          <button
            onClick={onBegin}
            data-testid="diagnosis-begin"
            className="group inline-flex items-center gap-3 rounded-full px-9 py-4 font-sans text-xs uppercase tracking-wider font-semibold transition-colors"
            style={{ background: "#C9A961", color: "#0A0A0A", boxShadow: "0 0 32px rgba(201,169,97,0.35)", letterSpacing: "0.15em" }}
          >
            Begin my diagnosis
            <Sparkles className="h-4 w-4" />
          </button>
          <Link to="/story" data-testid="intro-book-link" className="group inline-flex items-center gap-2 font-serif italic text-2xl text-ink hover:text-brass transition-colors">
            <BookOpen className="h-5 w-5" /> Prefer to read my story first?
          </Link>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.75 }} className="mt-8 inline-flex items-center gap-3 rounded-full border px-4 py-2 backdrop-blur" data-testid="intro-founder-chip" style={{ borderColor: "rgba(201,169,97,0.35)", background: "rgba(0,0,0,0.5)" }}>
          <img
            src="/founder/krishna-portrait-2026.jpg?v=3"
            alt="Krishna S"
            className="h-9 w-9 rounded-full object-cover object-[65%_35%] ring-1"
            style={{ boxShadow: "0 0 0 1px rgba(201,169,97,0.35)" }}
            data-testid="founder-avatar-diagnosis"
          />
          <div className="leading-tight">
            <p className="font-serif text-sm text-ink">Reviewed by Krishna</p>
            <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-ink-muted">Founder · Get Virtual Growth</p>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.9 }} className="mt-16 flex flex-wrap gap-x-6 gap-y-2 border-t pt-6" style={{ borderColor: "rgba(201,169,97,0.15)" }}>
          {REGIONS.map((r) => (
            <span key={r} className="font-sans text-[11px] uppercase tracking-[0.35em] text-ink-muted">{r}</span>
          ))}
        </motion.div>
      </div>
    </main>
  );
}

// Word-by-word wave — matches Scene 1 easing (organic cubic-bezier)
const DIAG_EASE = [0.2, 0.8, 0.2, 1];
function DiagWave({ text, className = "", delay = 0, goldWords = [] }) {
  const words = (text || "").split(/(\s+)/);
  const goldSet = new Set(goldWords);
  let idx = -1;
  return (
    <span className={className} aria-label={text}>
      {words.map((w, i) => {
        if (!w.trim()) return <React.Fragment key={i}>{w}</React.Fragment>;
        idx += 1;
        const clean = w.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
        const isGold = goldSet.has(clean);
        return (
          <motion.span
            key={i}
            aria-hidden
            className="inline-block will-change-transform"
            initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.42, ease: DIAG_EASE, delay: delay + idx * 0.05 }}
            style={isGold ? { color: "#C9A961" } : undefined}
          >
            {w}
          </motion.span>
        );
      })}
    </span>
  );
}

export default function Diagnosis() {
  usePageTitle("Diagnosis");
  const total = QUESTIONS.length;
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0); // 0..total-1 questions, then contact, then result
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", business_name: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  let stage;
  if (step < total) stage = "q";
  else if (step === total) stage = "contact";
  else stage = "result";
  const progress = Math.min(step / (total + 1), 1) * 100;

  const choose = (opt) => {
    setAnswers((a) => ({ ...a, [step]: opt }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const submit = async () => {
    if (!contact.name || !contact.email) {
      toast.error("Please add your name and email to receive the analysis.");
      return;
    }
    const score = Object.values(answers).reduce((sum, o) => sum + o.score, 0);
    const pct = Math.round((score / (total * 16)) * 100);
    const t = tierFor(pct);
    setLoading(true);
    try {
      await createDiagnosis({
        ...contact,
        score: pct,
        tier: t.tier,
        answers: QUESTIONS.map((qq, i) => ({ question: qq.q, answer: answers[i]?.label || "" })),
      });
      setResult({ pct, ...t });
      setStep(total + 1);
    } catch (e) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!started) return <Intro onBegin={() => setStarted(true)} />;

  return (
    <main className="min-h-screen pt-24 pb-20 flex flex-col relative" data-testid="diagnosis-page" style={{ background: "#000000" }}>
      {/* Champagne atmospherics behind the question flow */}
      <div
        aria-hidden
        className="pointer-events-none fixed -right-40 top-16 h-[620px] w-[620px] rounded-full opacity-[0.10] blur-[120px]"
        style={{ background: "radial-gradient(circle, rgba(201,169,97,0.9) 0%, rgba(201,169,97,0) 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed -left-40 bottom-0 h-[520px] w-[520px] rounded-full opacity-[0.08] blur-[110px]"
        style={{ background: "radial-gradient(circle, rgba(245,220,160,0.75) 0%, rgba(245,220,160,0) 70%)" }}
      />
      {/* Progress */}
      <div className="fixed top-20 inset-x-0 h-px z-40" style={{ background: "rgba(201,169,97,0.15)" }}>
        <motion.div className="h-full" style={{ background: "#C9A961" }} animate={{ width: `${progress}%` }} transition={{ ease: "easeOut" }} />
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-12 flex flex-col justify-center">
        <AnimatePresence mode="wait">
          {stage === "q" && (
            <motion.div
              key={`q-${step}`}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`question-${step}`}
            >
              <p className="font-sans text-[11px] uppercase tracking-[0.5em] text-brass mb-8">
                Question {step + 1} / {total}
              </p>
              <h2
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: "clamp(1.9rem, 6vw, 5.2rem)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.02em",
                  fontWeight: 500,
                  color: "#F5F1E8",
                }}
              >
                {QUESTIONS[step].q}
              </h2>
              <div className="mt-10 space-y-3">
                {QUESTIONS[step].options.map((opt, i) => {
                  const active = answers[step]?.label === opt.label;
                  return (
                    <button
                      key={i}
                      onClick={() => choose(opt)}
                      data-testid={`option-${step}-${i}`}
                      className={`group w-full text-left rounded-xl border px-6 py-5 font-sans text-lg transition-colors duration-300 flex items-center justify-between ${
                        active ? "border-forest bg-forest text-bg" : "border-line bg-surface text-ink hover:border-forest"
                      }`}
                    >
                      {opt.label}
                      <ArrowRight className={`h-5 w-5 transition-transform duration-300 ${active ? "translate-x-0" : "-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"}`} />
                    </button>
                  );
                })}
              </div>
              {step > 0 && (
                <button onClick={() => setStep((s) => s - 1)} className="mt-8 inline-flex items-center gap-2 font-sans text-sm text-ink-muted hover:text-forest" data-testid="back-btn">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
              )}
            </motion.div>
          )}

          {stage === "contact" && (
            <motion.div
              key="contact"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.5 }}
              data-testid="diagnosis-contact"
            >
              <p className="font-sans text-xs uppercase tracking-mega text-brass mb-6">Almost there</p>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-ink">Where shall I send your analysis?</h2>
              <p className="mt-4 font-sans text-ink-muted max-w-md">Your score is ready. Add your details and Krishna will follow up with a deeper look. Your information stays private and confidential.</p>
              <div className="mt-10 space-y-6">
                {[
                  { k: "name", label: "Your name", ph: "Your name", type: "text" },
                  { k: "email", label: "Email", ph: "you@company.com", type: "email" },
                  { k: "business_name", label: "Business name", ph: "Your company", type: "text" },
                ].map((f) => (
                  <label key={f.k} className="block">
                    <span className="font-sans text-xs uppercase tracking-wider text-ink-muted">{f.label}</span>
                    <input
                      type={f.type}
                      data-testid={`diag-${f.k}`}
                      value={contact[f.k]}
                      onChange={(e) => setContact((c) => ({ ...c, [f.k]: e.target.value }))}
                      placeholder={f.ph}
                      className="mt-2 w-full bg-transparent border-b border-line py-3 font-sans text-lg text-ink placeholder:text-ink-muted/40 outline-none focus:border-forest transition-colors"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-10 flex items-center gap-6">
                <button onClick={submit} disabled={loading} data-testid="diag-submit" className="group inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 font-sans text-xs uppercase tracking-wider font-semibold text-bg hover:bg-forest-hover transition-colors disabled:opacity-60">
                  {loading ? "Calculating..." : "Reveal my score"}
                  <Sparkles className="h-4 w-4" />
                </button>
                <button onClick={() => setStep(total - 1)} className="font-sans text-sm text-ink-muted hover:text-forest" data-testid="diag-back">Back</button>
              </div>
            </motion.div>
          )}

          {stage === "result" && result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="text-center"
              data-testid="diagnosis-result"
            >
              <p className="font-sans text-xs uppercase tracking-mega text-brass mb-6">Your growth health</p>
              <div className="relative mx-auto w-48 h-48">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="44" fill="none" stroke="#E5E5DF" strokeWidth="6" />
                  <motion.circle
                    cx="50" cy="50" r="44" fill="none" stroke="#2B4A3B" strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={276}
                    initial={{ strokeDashoffset: 276 }}
                    animate={{ strokeDashoffset: 276 - (276 * result.pct) / 100 }}
                    transition={{ duration: 1.4, ease: "easeOut" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-serif text-6xl text-ink">{result.pct}</span>
                  <span className="font-sans text-xs uppercase tracking-wider text-ink-muted">/ 100</span>
                </div>
              </div>
              <h2 className="mt-8 font-serif text-4xl md:text-5xl text-forest">{result.tier}</h2>
              <p className="mt-4 font-sans text-ink-muted max-w-md mx-auto leading-relaxed">{result.note}</p>
              <div className="mt-10 rounded-2xl border border-line bg-surface p-8 max-w-lg mx-auto">
                <div className="flex justify-center mb-5">
                  <KrishnaLoop variant="avatar" size={72} />
                </div>
                <p className="font-serif italic text-2xl text-ink">
                  “This score is only the surface. Give me a few days and I’ll send your full written diagnosis.”
                </p>
                <p className="mt-4 font-sans text-xs uppercase tracking-mega text-ink-muted">, Krishna S, Founder</p>
              </div>
              <Link
                to="/story"
                data-testid="result-book-link"
                className="group mt-8 inline-flex items-center gap-2 font-serif italic text-2xl text-ink hover:text-forest transition-colors"
              >
                <BookOpen className="h-5 w-5" /> While you wait, read my little book
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}